<template>
  <div id="comments">
    <div class="comments-section mt-3">
      <div v-if="commentNotAllowed" class="warning-block d-flex align-center mt-9 ml-7 mr-7 mb-9">
        <v-icon class="mr-3 ml-4" icon="$info" />
        <span class="body2">{{ $t("comments.noPermission") }}</span>
      </div>

      <coro-comment-input v-else :ticket="ticket" @add-comment="onAddComment" />

      <div v-if="comments.length">
        <div
          v-for="item in comments"
          :key="item.id"
          class="comment-item"
          :class="{ 'comment-item--confidential': item.confidential }"
        >
          <div class="d-flex align-center">
            <div class="mr-2">
              <v-icon size="35" icon="$user" />
            </div>
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <div class="subtitle1 text-no-wrap comment-title mr-2">
                  {{ item.createdBy }}
                </div>
                <div class="caption text-no-wrap">
                  {{ formatDistanceToNow(new Date(item.createdAt)) }}
                </div>
                <v-tooltip v-if="getCommentRecipients(item).length" open-delay="300" location="top">
                  <template #activator="{ props }">
                    <div class="comment-recipients text-no-wrap" v-bind="props">
                      <span class="comment-recipients--separator mr-1 ml-2">|</span>
                      <v-icon size="16" icon="$forward" />
                      <span class="caption">{{ getCommentRecipients(item).length }}</span>
                    </div>
                  </template>
                  <div class="px-3 py-1">
                    <div class="text--white text--semibold">
                      {{ $t("comments.sentTo") }}
                    </div>
                    <ul>
                      <li
                        v-for="recipient in getCommentRecipients(item)"
                        :key="recipient"
                        class="text--white"
                      >
                        {{ recipient }}
                      </li>
                    </ul>
                  </div>
                </v-tooltip>
              </div>
              <div class="text-no-wrap d-flex align-center">
                <v-chip
                  v-if="item.confidential"
                  label
                  size="x-small"
                  color="indigo-medium"
                  variant="flat"
                >
                  <v-icon size="12" icon="$confidential" />
                  <span class="font-weight-bold text-white">{{ $t("comments.confidential") }}</span>
                </v-chip>

                <slot name="comment-actions" :comment="item" />
              </div>
            </div>
          </div>
          <coro-comment-expander class="pl-11 pt-3" :text="item.message" :max-length="240" />
        </div>
        <div class="d-flex justify-center mt-3 mb-3">
          <v-btn
            v-if="commentsToLoad > 0"
            variant="text"
            color="primary"
            :loading="loading"
            :disabled="loading"
            class="text-uppercase font-weight-bold"
            @click="onLoadMore"
          >
            {{ $t("comments.plusNComments", { n: commentsToLoad }) }}
          </v-btn>
        </div>
      </div>
      <div v-else class="d-flex flex-column align-center justify-center pa-7">
        <v-icon size="80" icon="$comments" />
        <span class="subtitle1">{{ $t("comments.noComments") }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import CoroCommentInput from "@/components/CoroCommentInput.vue";
import CoroCommentExpander from "@/components/CoroCommentExpander.vue";
import { type Comment, type CommentSettings } from "@/_store/ticket-comments.module";
import { useI18n } from "vue-i18n";
import moment, { type MomentInput } from "moment/moment";
import type { TicketDetails } from "@/_store/tickets/tickets.module.ts";

export default defineComponent({
  components: {
    CoroCommentInput,
    CoroCommentExpander,
  },
  props: {
    ticket: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    commentNotAllowed: {
      type: Boolean,
      default: false,
    },
    totalComments: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["add-comment", "load-more"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const commentsToLoad = computed(() =>
      Math.max(props.totalComments - (props.comments?.length ?? 0), 0)
    );

    const getSelectedRecipientCheckboxes = (item: Comment & CommentSettings) => {
      const keysToCheck = [
        "notifyAffectedUsers",
        "notifyAllWorkspaceAdmins",
        "notifySocContacts",
        "notifyCoroAdmins",
      ] as const;
      return keysToCheck.filter((key) => item[key] === true);
    };

    const getCommentRecipients = (item: Comment) => {
      const notifyCheckboxList = getSelectedRecipientCheckboxes(item as Comment & CommentSettings);
      return [
        ...notifyCheckboxList.map((v) => t(`comments.${v}`)),
        ...(item.customRecipients ?? []),
      ];
    };

    const formatDistanceToNow = (date: MomentInput) => moment(date).fromNow();

    const onAddComment = (
      payload: CommentSettings & {
        eventId: string;
        workspaceId?: string;
      }
    ) => {
      emit("add-comment", payload);
    };

    const onLoadMore = () => emit("load-more");

    return {
      getCommentRecipients,
      formatDistanceToNow,
      onAddComment,
      onLoadMore,
      commentsToLoad,
    };
  },
});
</script>

<style scoped lang="scss">
.comment-item {
  padding: 16px 8px 16px 0;

  &--confidential {
    background-color: rgb(var(--v-theme-indigo-faint));
    border-bottom: 1px solid rgb(var(--v-theme-white));
  }
}

.comment-recipients {
  height: 24px;
  white-space: nowrap;

  &--separator {
    color: rgb(var(--v-theme-indigo-faint)) !important;
  }
}

.comment-border {
  border-bottom: 1px solid rgb(var(--v-theme-indigo-faint));
}

.v-chip--label {
  padding-left: 3px;
  padding-right: 3px;
}

.icon-chevron-down-xs:before {
  color: white !important;
  font-size: 16px;
}
</style>
